import React from 'react';
import { useNavigate } from 'react-router-dom';
import './ITServicesPage.css';

const ITServicesPage = () => {
  const navigate = useNavigate();

  const services = [
    {
      title: 'Web Development',
      icon: '🌐',
      description: 'Create stunning, responsive websites that engage your audience and drive results.',
      features: [
        'Custom website design and development',
        'Responsive and mobile-first approach',
        'SEO-optimized architecture',
        'Content Management Systems (CMS)',
        'E-commerce solutions',
        'Progressive Web Applications (PWA)'
      ]
    },
    {
      title: 'Custom Web Apps',
      icon: '⚙️',
      description: 'Tailored web applications built to solve your unique business challenges.',
      features: [
        'Custom business logic implementation',
        'Scalable application architecture',
        'Real-time data processing',
        'Third-party API integrations',
        'User authentication & authorization',
        'Dashboard and analytics solutions'
      ]
    },
    {
      title: 'Process Automation',
      icon: '🔄',
      description: 'Streamline operations and boost efficiency with intelligent automation solutions.',
      features: [
        'Workflow automation',
        'Document processing automation',
        'Email and notification systems',
        'Data synchronization',
        'Scheduled task management',
        'Integration with existing tools'
      ]
    },
    {
      title: 'Cloud Migration',
      icon: '☁️',
      description: 'Seamlessly transition your infrastructure to the cloud for better scalability and reliability.',
      features: [
        'Cloud strategy and planning',
        'AWS, Azure, and Google Cloud expertise',
        'Data migration and security',
        'Infrastructure as Code (IaC)',
        'Cost optimization',
        'Ongoing cloud management'
      ]
    }
  ];

  const technologies = [
    { name: 'React', category: 'Frontend' },
    { name: 'Node.js', category: 'Backend' },
    { name: 'Python', category: 'Backend' },
    { name: 'AWS', category: 'Cloud' },
    { name: 'Azure', category: 'Cloud' },
    { name: 'Docker', category: 'DevOps' },
    { name: 'PostgreSQL', category: 'Database' },
    { name: 'MongoDB', category: 'Database' },
    { name: 'REST APIs', category: 'Integration' },
    { name: 'GraphQL', category: 'Integration' }
  ];

  const benefits = [
    {
      title: 'Scalable Solutions',
      description: 'Build applications that grow with your business',
      icon: '📈'
    },
    {
      title: 'Modern Technology',
      description: 'Leverage the latest tech stack and best practices',
      icon: '⚡'
    },
    {
      title: 'Security First',
      description: 'Enterprise-grade security in every solution',
      icon: '🔒'
    },
    {
      title: '24/7 Support',
      description: 'Round-the-clock technical support and maintenance',
      icon: '🛟'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Discovery',
      description: 'We understand your business needs, challenges, and goals'
    },
    {
      step: '02',
      title: 'Planning',
      description: 'Strategic roadmap with timelines, milestones, and deliverables'
    },
    {
      step: '03',
      title: 'Development',
      description: 'Agile development with regular updates and feedback loops'
    },
    {
      step: '04',
      title: 'Deployment',
      description: 'Seamless launch with thorough testing and quality assurance'
    },
    {
      step: '05',
      title: 'Support',
      description: 'Ongoing maintenance, updates, and technical support'
    }
  ];

  return (
    <div className="it-services-page">
      {/* Hero Section */}
      <section className="it-hero">
        <div className="container">
          <button className="back-btn" onClick={() => navigate('/')}>
            ← Back to Home
          </button>
          <div className="hero-content">
            <h1 className="hero-title">
              IT Services & Solutions
            </h1>
            <p className="hero-subtitle">
              Cutting-edge technology solutions that transform businesses and drive digital innovation
            </p>
            <div className="hero-stats">
              <div className="stat">
                <h3>50+</h3>
                <p>Projects Delivered</p>
              </div>
              <div className="stat">
                <h3>98%</h3>
                <p>Client Satisfaction</p>
              </div>
              <div className="stat">
                <h3>24/7</h3>
                <p>Support Available</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="services-detail">
        <div className="container">
          <h2 className="section-title">What We Offer</h2>
          <p className="section-subtitle">
            Comprehensive IT services tailored to your business needs
          </p>
          <div className="services-grid">
            {services.map((service, index) => (
              <div key={index} className="service-detail-card">
                <div className="service-icon-large">{service.icon}</div>
                <h3>{service.title}</h3>
                <p className="service-desc">{service.description}</p>
                <ul className="features-list">
                  {service.features.map((feature, idx) => (
                    <li key={idx}>
                      <span className="checkmark">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="technologies">
        <div className="container">
          <h2 className="section-title">Our Technology Stack</h2>
          <p className="section-subtitle">
            We work with industry-leading technologies
          </p>
          <div className="tech-grid">
            {technologies.map((tech, index) => (
              <div key={index} className="tech-item">
                <span className="tech-name">{tech.name}</span>
                <span className="tech-category">{tech.category}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="benefits">
        <div className="container">
          <h2 className="section-title">Why Choose Us</h2>
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-card">
                <div className="benefit-icon">{benefit.icon}</div>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="process">
        <div className="container">
          <h2 className="section-title">Our Process</h2>
          <p className="section-subtitle">
            A proven methodology for successful project delivery
          </p>
          <div className="process-timeline">
            {process.map((item, index) => (
              <div key={index} className="process-step">
                <div className="step-number">{item.step}</div>
                <div className="step-content">
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Transform Your Business?</h2>
            <p>Let's discuss how our IT services can help you achieve your goals</p>
            <div className="cta-buttons">
              <button className="primary-btn" onClick={() => navigate('/#contact')}>
                Get Started
              </button>
              <button className="secondary-btn" onClick={() => navigate('/')}>
                Explore More Services
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ITServicesPage;
